/**
 * Single source of truth for each family's decrypted Binder categories.
 *
 * Previously every consumer (the sidebar rail, the Binder pages, the document
 * detail) ran its own `useFamilyCategories` fetch, so mounting a Binder page
 * with the rail already up fired the `/orgs/{id}/categories` endpoint several
 * times over, and each re-fetched all families again whenever a family key
 * hydrated. This provider centralizes that into one fetch per family per
 * session and adds a stale-while-revalidate cache so category names paint
 * from local storage instantly on unlock — no network round-trip before the
 * family UI shows.
 *
 * Caching honors zero-knowledge at rest: only the server's *encrypted*
 * CategorySummary blobs are persisted (see `storage/safe-store.ts`); decryption
 * happens in memory with the family key. Staleness is bounded to the session —
 * the network revalidation runs once per family per unlock, which matches the
 * product's "eventual consistency on next unlock" tolerance for scope changes.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import type { CategorySummary } from '@heybesafe/shared';
import { useSafe } from './SafeContext';
import { useFamilyKeys } from './FamilyKeysContext';
import { listCategories } from '../api/client';
import { decryptCategoryName } from '../crypto/categoryName';
import { base64UrlToBytes } from '../crypto/base64url';
import { getCachedCategories, setCachedCategories } from '../storage/safe-store';

export interface DecryptedCategory {
  categoryId: string;
  name: string;
  icon?: string;
  circleId?: string;
  order: number;
}

interface FamilyCategoriesValue {
  byOrg: Map<string, DecryptedCategory[]>;
  /** Invalidate a family (or all families) and re-fetch — call after category
   *  mutations so the rail + cache reflect the change. */
  refresh(orgId?: string): void;
}

const FamilyCategoriesContext = createContext<FamilyCategoriesValue | null>(null);

async function decryptSummaries(
  summaries: CategorySummary[],
  privKey: Uint8Array,
  pubBytes: Uint8Array,
): Promise<DecryptedCategory[]> {
  const out: DecryptedCategory[] = [];
  for (const summary of summaries) {
    try {
      const { name, icon } = await decryptCategoryName(summary, privKey, pubBytes);
      out.push({ categoryId: summary.categoryId, name, icon, circleId: summary.circleId, order: summary.order });
    } catch {
      // Undecryptable (wrong key / corrupt blob) — skip; never break the rail.
    }
  }
  out.sort((a, b) => a.order - b.order);
  return out;
}

export function FamilyCategoriesProvider({ children }: { children: ReactNode }) {
  const { orgs = [] } = useSafe();
  const { getKey, version } = useFamilyKeys();
  const [byOrg, setByOrg] = useState<Map<string, DecryptedCategory[]>>(new Map());
  // Families whose network revalidation has completed this session. Guards
  // against re-fetching every family on each key-hydration `version` bump.
  const revalidated = useRef<Set<string>>(new Set());
  const [tick, setTick] = useState(0);

  const orgKey = orgs.map((o) => o.id).join(',');

  const refresh = useCallback((orgId?: string) => {
    if (orgId) revalidated.current.delete(orgId);
    else revalidated.current.clear();
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      orgs.map(async (org) => {
        if (revalidated.current.has(org.id)) return; // already have live data this session
        const privKey = getKey(org.id);
        if (!privKey || !org.publicKey) return; // key not hydrated yet — retry on next version bump
        const pubBytes = base64UrlToBytes(org.publicKey);

        // 1. Instant paint from the local cache (zero-knowledge ciphertext at rest).
        const cached = getCachedCategories(org.id);
        if (cached && !cancelled) {
          const seeded = await decryptSummaries(cached, privKey, pubBytes);
          if (!cancelled) setByOrg((prev) => new Map(prev).set(org.id, seeded));
        }

        // 2. Revalidate from the network once per family per session.
        const res = await listCategories(org.id);
        if (cancelled || !res.ok) return; // leave any cache-seeded data; retry next unlock
        revalidated.current.add(org.id);
        const fresh = await decryptSummaries(res.data.categories, privKey, pubBytes);
        if (!cancelled) setByOrg((prev) => new Map(prev).set(org.id, fresh));
        setCachedCategories(org.id, res.data.categories);
      }),
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgKey, version, tick]);

  const value = useMemo(() => ({ byOrg, refresh }), [byOrg, refresh]);
  return <FamilyCategoriesContext.Provider value={value}>{children}</FamilyCategoriesContext.Provider>;
}

/** The decrypted categories for every family the user belongs to, by orgId. */
export function useFamilyCategories(): Map<string, DecryptedCategory[]> {
  const ctx = useContext(FamilyCategoriesContext);
  if (!ctx) throw new Error('useFamilyCategories must be used within a FamilyCategoriesProvider');
  return ctx.byOrg;
}

/** Invalidate + re-fetch a family's categories (or all). Use after a mutation. */
export function useRefreshFamilyCategories(): (orgId?: string) => void {
  const ctx = useContext(FamilyCategoriesContext);
  if (!ctx) throw new Error('useRefreshFamilyCategories must be used within a FamilyCategoriesProvider');
  return ctx.refresh;
}
